import React, { useState } from 'react';
import { backendServices } from '../../services/backend.service';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

// Form data interface (without server-generated fields)
interface AuditLogFormData {
  action: string;
  category: 'user' | 'asset' | 'compliance' | 'settings' | 'wallet' | 'report' | 'system';
  actor: string;
  details: string;
  ipAddress?: string;
  severity: 'info' | 'warning' | 'critical';
}

// Full audit log interface (includes server-generated fields)
interface AuditLog extends AuditLogFormData {
  id: string;
  timestamp: Date;
  metadata?: Record<string, unknown>;
}

// API response interface
interface ApiResponse<T = unknown> {
  status: boolean;
  message: string;
  data?: T;
  error?: string;
}

interface AddAuditLogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogAdded: (log: AuditLog) => void;
}

export const AddAuditLogModal: React.FC<AddAuditLogModalProps> = ({
  open,
  onOpenChange,
  onLogAdded,
}) => {
  const [formData, setFormData] = useState<AuditLogFormData>({
    action: '',
    category: 'user',
    actor: '',
    details: '',
    ipAddress: '',
    severity: 'info',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Call backend API to create audit log with server-generated ID and timestamp
      const response = await backendServices.post<ApiResponse<AuditLog>>('/audit', {
        action: formData.action,
        category: formData.category,
        actor: formData.actor || 'Admin',
        details: formData.details,
        ipAddress: formData.ipAddress || 'System',
        severity: formData.severity,
      });

      const apiResponse = response.data as unknown as ApiResponse<Record<string, unknown>>;

      if (apiResponse.status && apiResponse.data) {
        const d = apiResponse.data;
        const mapped: AuditLog = {
          id: String(d._id),
          action: String(d.action),
          category: d.category as AuditLog['category'],
          actor: String(d.actor),
          details: String(d.details),
          ipAddress: d.ipAddress != null ? String(d.ipAddress) : 'System',
          timestamp: d.timestamp ? new Date(d.timestamp as string) : new Date(),
          severity: (d.severity as AuditLog['severity']) ?? 'info',
        };
        onLogAdded(mapped);
      } else {
        throw new Error(apiResponse.message || 'Failed to create audit log');
      }

      // Reset form
      setFormData({
        action: '',
        category: 'user',
        actor: '',
        details: '',
        ipAddress: '',
        severity: 'info',
      });

      onOpenChange(false);
    } catch (error) {
      alert('Failed to create audit log. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md mx-4 sm:mx-auto'>
        <DialogHeader>
          <DialogTitle>Add Audit Log Entry</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='action'>Action</Label>
            <Input
              id='action'
              value={formData.action}
              onChange={(e) => handleInputChange('action', e.target.value)}
              placeholder='e.g., User Login, Asset Created'
              required
            />
          </div>

          <div className='grid grid-cols-2 gap-3 sm:gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='category'>Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  handleInputChange('category', value as AuditLog['category'])
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='user'>User</SelectItem>
                  <SelectItem value='asset'>Asset</SelectItem>
                  <SelectItem value='compliance'>Compliance</SelectItem>
                  <SelectItem value='settings'>Settings</SelectItem>
                  <SelectItem value='wallet'>Wallet</SelectItem>
                  <SelectItem value='report'>Report</SelectItem>
                  <SelectItem value='system'>System</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='severity'>Severity</Label>
              <Select
                value={formData.severity}
                onValueChange={(value) =>
                  handleInputChange('severity', value as AuditLog['severity'])
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='info'>Info</SelectItem>
                  <SelectItem value='warning'>Warning</SelectItem>
                  <SelectItem value='critical'>Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='actor'>Actor</Label>
            <Input
              id='actor'
              value={formData.actor}
              onChange={(e) => handleInputChange('actor', e.target.value)}
              placeholder='e.g., Admin (john@vestn.io)'
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='details'>Details</Label>
            <Textarea
              id='details'
              value={formData.details}
              onChange={(e) => handleInputChange('details', e.target.value)}
              placeholder='Detailed description of the action...'
              rows={3}
              required
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='ipAddress'>IP Address</Label>
            <Input
              id='ipAddress'
              value={formData.ipAddress}
              onChange={(e) => handleInputChange('ipAddress', e.target.value)}
              placeholder='e.g., 192.168.1.100 or System'
            />
          </div>

          <div className='flex justify-end gap-2 pt-4'>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Add Log Entry'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
